// JS for LPv2 Main Product Add to Cart / Notify Me Buttons block
export function initButtonsBlock() {
  let addToCartButtonWasSeen = false;

  // Get the state manager for product data
  const stateManager = window.lpv2MainProductStateManager;
  if (!stateManager) return;

  // Get the main buttons container
  const buttonsContainer = document.querySelector('.lpv2-main-product-buttons');
  if (!buttonsContainer) return;

  // Read sticky setting from data attribute
  const enableSticky = buttonsContainer.getAttribute('data-enable-sticky') === 'true';

  // Get all required DOM elements
  const addToCartBtn = buttonsContainer.querySelector('.lpv2-btn--add-to-cart');
  const stickyBtn = document.querySelector('.lpv2-sticky-add-to-cart .lpv2-add-to-cart-btn[data-sticky]');
  const notifyMeBtn = buttonsContainer.querySelector('.lpv2-btn--notify-me');
  const form = buttonsContainer.querySelector('#lpv2-add-to-cart-form');
  const variantInput = form ? form.querySelector('input[name="id"]') : null;
  const quantityInput = form ? form.querySelector('input[name="quantity"]') : null;
  const sellingPlanInput = form ? form.querySelector('input[name="selling_plan"]') : null;
  const priceSpan = addToCartBtn ? addToCartBtn.querySelector('.lpv2-btn__price') : null;
  const compareAtSpan = addToCartBtn ? addToCartBtn.querySelector('.lpv2-btn__compare_at_price') : null;

  /**
   * Format money amount from cents to currency string
   * @param {number} cents - Amount in cents
   * @param {string} currency - Currency code (default: 'GBP')
   * @param {string} locale - Locale string (default: 'en-GB')
   * @returns {string} Formatted currency string
   */
  function formatMoney(cents, currency = 'GBP', locale = 'en-GB') {
    if (typeof cents !== 'number') return '';
    
    // Use Shopify's built-in money formatting if available
    if (window.Shopify && window.Shopify.formatMoney && window.moneyFormat) {
      return Shopify.formatMoney(cents, window.moneyFormat);
    }
    
    // Fallback to Intl.NumberFormat with provided currency and locale
    return (cents / 100).toLocaleString(locale, { style: 'currency', currency });
  }

  // Track when add to cart button becomes visible for sticky behavior
  if (addToCartBtn) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            addToCartButtonWasSeen = true;
            observer.disconnect();
          }
        });
      },
      {
        threshold: 0.01 // Button only needs to be a tiny bit visible
      }
    );
    observer.observe(addToCartBtn);
  }

  /**
   * Update button states and form inputs based on current product state
   * @param {Object} state - Current product state from state manager
   */
  function updateButtons(state) {
    if (!addToCartBtn || !notifyMeBtn) return;

    // Read continueSellingWhenOutOfStock directly from the DOM JSON we created in skio-picker if possible
    let canContinueOverselling = false;
    if (state.selectedVariantId) {
      try {
        const dataEls = document.querySelectorAll('script[id^="lpv2-preorder-data-"]');
        if (dataEls.length > 0) {
          const variantPreorderData = JSON.parse(dataEls[0].textContent);
          canContinueOverselling = variantPreorderData[String(state.selectedVariantId)] && variantPreorderData[String(state.selectedVariantId)].continueSellingWhenOutOfStock === true;
        }
      } catch(e) {}
    }

    // Check if product is available and has a selected variant
    const isAvailable = canContinueOverselling || (state.selectedVariant && state.selectedVariant.available);
    const hasVariant = state.selectedVariant && state.selectedVariantId;

    // Show/hide buttons based on availability
    if (isAvailable && hasVariant) {
      addToCartBtn.style.display = '';
      notifyMeBtn.style.display = 'none';
    } else {
      addToCartBtn.style.display = 'none';
      notifyMeBtn.style.display = '';
    }

    // Testing mode - show both buttons
    const testing = false;
    if (testing) {
      addToCartBtn.style.display = '';
      notifyMeBtn.style.display = '';
    }

    // Update form inputs with current state
    if (variantInput && state.selectedVariantId) {
      variantInput.value = state.selectedVariantId;
    }
    if (quantityInput && typeof state.quantity === 'number') {
      if (parseInt(quantityInput.value, 10) !== state.quantity) {
        quantityInput.value = state.quantity;
      }
    }
    if (sellingPlanInput) {
      sellingPlanInput.value = state.selectedPlan ? state.selectedPlan.id : '';
    }

    // Update price display
    if (priceSpan && typeof state.discountedPrice === 'number') {
      priceSpan.textContent = formatMoney(state.discountedPrice, state.currency, state.locale);
      priceSpan.dataset.basePrice = formatMoney(state.discountedPrice, state.currency, state.locale);
    }

    // Update compare at price display
    if (compareAtSpan) {
      if (typeof state.compareAtPrice === 'number' && state.compareAtPrice > state.discountedPrice) {
        addToCartBtn.classList.add('has-compare-price');
        compareAtSpan.textContent = formatMoney(state.compareAtPrice, state.currency, state.locale);
        compareAtSpan.style.display = '';
        compareAtSpan.dataset.baseComparePrice = formatMoney(state.compareAtPrice, state.currency, state.locale);
      } else {
        addToCartBtn.classList.remove('has-compare-price');
        compareAtSpan.style.display = 'none';
      }
    } else if (addToCartBtn) {
      if (typeof state.compareAtPrice === 'number' && state.compareAtPrice > state.discountedPrice) {
        addToCartBtn.classList.add('has-compare-price');
      } else {
        addToCartBtn.classList.remove('has-compare-price');
      }
    }

    // Enable/disable add to cart button based on variant selection
    if (!hasVariant) {
      addToCartBtn.disabled = true;
    } else {
      addToCartBtn.disabled = false;
    }
  }

  /**
   * Set loading state for add to cart buttons
   * @param {boolean} isLoading - Whether to show loading state
   */
  function setAddToCartSkeleton(isLoading) {
    // Update main add to cart button
    if (addToCartBtn) {
      addToCartBtn.classList.toggle('lpv2-btn--skeleton', isLoading);
      addToCartBtn.disabled = isLoading;
      addToCartBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      
      if (isLoading) {
        if (!addToCartBtn.querySelector('.lpv2-btn__skeleton-overlay')) {
          const overlay = document.createElement('span');
          overlay.className = 'lpv2-btn__skeleton-overlay';
          overlay.textContent = 'Adding to cart...';
          addToCartBtn.appendChild(overlay);
        }
      } else {
        const overlay = addToCartBtn.querySelector('.lpv2-btn__skeleton-overlay');
        if (overlay) overlay.remove();
      }
    }

    // Update sticky add to cart button
    const stickyBtn = document.querySelector('.lpv2-sticky-add-to-cart .lpv2-add-to-cart-btn[data-sticky]');
    if (stickyBtn) {
      stickyBtn.classList.toggle('lpv2-btn--skeleton', isLoading);
      stickyBtn.disabled = isLoading;
      stickyBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      
      if (isLoading) {
        if (!stickyBtn.querySelector('.lpv2-btn__skeleton-overlay')) {
          const overlay = document.createElement('span');
          overlay.className = 'lpv2-btn__skeleton-overlay';
          overlay.textContent = 'Adding to cart...';
          stickyBtn.appendChild(overlay);
        }
      } else {
        const overlay = stickyBtn.querySelector('.lpv2-btn__skeleton-overlay');
        if (overlay) overlay.remove();
      }
    }

    // Update bundle button if present
    const bundleBtn = document.querySelector('.lpv2-bundle-btn');
    if (bundleBtn) {
      bundleBtn.classList.toggle('lpv2-btn--skeleton', isLoading);
      bundleBtn.disabled = isLoading;
      bundleBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }
  }

  /**
   * Handle add to cart button click
   * @param {Event} e - Click event
   */
  function handleAddToCart(e) {
    e.preventDefault();
    
    // Get current product state
    const state = stateManager.getState();
    
    // Validate that we have a selected and available variant
    if (!state.selectedVariant || !state.selectedVariant.available) {
      return;
    }

    // Disable buttons during submission to prevent double-clicks
    if (addToCartBtn) addToCartBtn.disabled = true;
    const stickyButton = document.querySelector('.lpv2-sticky-add-to-cart .lpv2-add-to-cart-btn');
    if (stickyButton) stickyButton.disabled = true;

    // Determine quantity from quantity input if present, fallback to state.quantity, fallback to 1
    const rawInputQty = quantityInput ? parseInt(quantityInput.value, 10) : null;
    const finalQuantity = (!isNaN(rawInputQty) && rawInputQty > 0) ? rawInputQty : (state.quantity || 1);

    // Prepare cart items array starting with the main product
    const cartItems = [{
      id: state.selectedVariantId,
      quantity: finalQuantity
    }];

    // Add selling plan if this is a subscription purchase
    const hasOptionsBundle = document.querySelector('.lpv2-main-product-options-bundle') !== null;

    if (!hasOptionsBundle) {
      // Standard product logic: exact original implementation
      if (state.selectedPlan && state.purchaseOption === 'subscription') {
        cartItems[0].selling_plan = state.selectedPlan.id;
      }
    } else {
      // Options bundle block logic
      if (state.purchaseOption === 'subscription') {
        const planId =
          state.selectedPlan?.id ||
          state.selectedPlanId ||
          (sellingPlanInput && sellingPlanInput.value) ||
          null;

        if (planId && planId !== '0' && planId !== 0) {
          const allocations = state.selectedVariant?.selling_plan_allocations;
          if (Array.isArray(allocations)) {
            if (allocations.length > 0) {
              const isAllocated = allocations.some(spa => {
                const spaId = spa.selling_plan_id || (spa.selling_plan && spa.selling_plan.id);
                return String(spaId) === String(planId);
              });
              if (isAllocated) {
                cartItems[0].selling_plan = Number(planId) || planId;
              } else {
                // Fallback to the first allocated selling plan for this variant
                const firstAllocId = allocations[0].selling_plan_id || (allocations[0].selling_plan && allocations[0].selling_plan.id);
                if (firstAllocId) {
                  cartItems[0].selling_plan = Number(firstAllocId) || firstAllocId;
                }
              }
            } else {
              console.warn(`[LPv2] Variant ${state.selectedVariantId} has no selling plan allocations in Shopify admin. Omitting selling_plan to prevent 422 error.`);
            }
          } else {
            cartItems[0].selling_plan = Number(planId) || planId;
          }
        }
      } else if (state.purchaseOption !== 'onetime') {
        const formPlanId = (sellingPlanInput && sellingPlanInput.value) || state.selectedPlanId;
        if (formPlanId && formPlanId !== '0' && formPlanId !== 0) {
          cartItems[0].selling_plan = Number(formPlanId) || formPlanId;
        }
      }
    }

    // Add freebies as separate cart items (only for subscription purchases and when enabled)
    const enableFreebiesSwiper = window.LPV2MainProductInitialState?.enableFreebiesSwiper;
    const freebiesEnabled = !hasOptionsBundle && enableFreebiesSwiper !== false && enableFreebiesSwiper !== "false";

    if (state.purchaseOption === 'subscription' && state.selectedFreebies && state.selectedFreebies.length > 0 && state.freebies && freebiesEnabled) {
      state.selectedFreebies.forEach(freebieId => {
        // Handle both string and number IDs, and both 'id' and 'variantId' properties
        const freebie = state.freebies.find(f => 
          String(f.id) === String(freebieId) || 
          String(f.variantId) === String(freebieId)
        );
        
        if (freebie) {
          // Check if freebie is available
          if (freebie.available !== false) { // Allow if available is true or undefined
            const freebieVariantId = freebie.id || freebie.variantId;
            cartItems.push({
              id: freebieVariantId,
              quantity: finalQuantity // Same quantity as main product
            });
          }
        }
      });
    }


    /**
     * Execute the actual add to cart request
     */
    function doAddToCart() {
      // Show loading state
      setAddToCartSkeleton(true);
      
      // Get cart drawer for sections and focus management
      const cartDrawerEl = document.querySelector('cart-drawer');
      
      // Set active element for focus management when cart drawer opens
      if (cartDrawerEl) {
        cartDrawerEl.setActiveElement(document.activeElement);
      }

      // Build JSON payload for /cart/add.js.
      // NOTE: We do NOT request sections here because Shopify ignores the `sections`
      // parameter in JSON body /cart/add.js and returns wrong section keys (e.g. 'product'
      // instead of 'cart-drawer'). We fetch sections separately via /cart/update.js.
      const jsonItems = cartItems.map((item, index) => {
        const cartItem = {
          id: item.id,
          quantity: item.quantity,
        };
        if (item.selling_plan && item.selling_plan !== '0' && item.selling_plan !== 0) {
          cartItem.selling_plan = Number(item.selling_plan) || item.selling_plan;
        }
        // Collect line item properties from the form for the first (main) item
        if (index === 0 && form) {
          const propertyInputs = form.querySelectorAll('input[name^="properties["]');
          const properties = {};
          propertyInputs.forEach(propInput => {
            const match = propInput.name.match(/properties\[(.+)\]/);
            if (match && propInput.value) {
              properties[match[1]] = propInput.value;
            }
          });
          if (Object.keys(properties).length > 0) {
            cartItem.properties = properties;
          }
        }
        return cartItem;
      });

      console.log('[LPv2 ATC Payload]', JSON.stringify({ items: jsonItems }));

      // Step 1: Add items to cart using current market routes
      const rawAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
      const cartAddUrl = rawAddUrl.endsWith('.js') ? rawAddUrl : rawAddUrl + '.js';
      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      const rawCartUrl = (window.routes && window.routes.cart_url) || '/cart';
      const cartJsUrl = rawCartUrl.endsWith('.js') ? rawCartUrl : rawCartUrl + '.js';

      fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items: jsonItems }),
      })
      .then((response) => {
        if (!response.ok) {
          return response.clone().json().then(errorData => {
            console.error('[LPv2] Cart API Error:', errorData);
            throw errorData;
          }).catch(err => {
            if (err && (err.description || err.message || err.status)) throw err;
            return response.text().then(text => {
              console.error('[LPv2] Cart API Error (text):', text);
              throw new Error(text);
            });
          });
        }
        return response.json();
      })
      .then((addData) => {
        if (addData.status) {
          console.error('[LPv2] Cart API returned error status:', addData);
          throw new Error(addData.description || 'Cart add failed');
        }

        const sectionsToRender = cartDrawerEl ? cartDrawerEl.getSectionsToRender() : [];

        return Promise.all([
          fetch(cartJsUrl, {
            headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
          }).then((r) => r.json()),
          ...sectionsToRender.map((s) => {
            const secUrl = `${cartUrl}?section_id=${s.id}`;
            return fetch(secUrl).then((r) => r.text());
          }),
        ]).then(async ([cartData, ...sectionHTMLs]) => {
          const isEuMarket = (window.routes && window.routes.root_url && window.routes.root_url.includes('-eu')) || (window.shopCurrency === 'EUR');
          if (isEuMarket && cartData.currency !== 'EUR') {
            const fd = new FormData();
            fd.append('country_code', 'DE');
            fd.append('_method', 'PUT');
            await fetch('/localization', { method: 'POST', body: fd, headers: { 'X-Requested-With': 'XMLHttpRequest' } }).catch(() => {});
            
            // Re-fetch cart and sections in EUR
            const [freshCartData, ...freshSectionHTMLs] = await Promise.all([
              fetch(cartJsUrl, { headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' } }).then(r => r.json()),
              ...sectionsToRender.map((s) => fetch(`${cartUrl}?section_id=${s.id}`).then(r => r.text())),
            ]);
            cartData = freshCartData;
            sectionHTMLs = freshSectionHTMLs;
          }

          const sections = {};
          sectionsToRender.forEach((s, i) => {
            sections[s.id] = sectionHTMLs[i];
          });
          return { ...cartData, sections };
        });
      })
      .then((cartDataWithSections) => {
        // Hide loading state and re-enable buttons
        setAddToCartSkeleton(false);
        if (addToCartBtn) addToCartBtn.disabled = false;
        const stickyButton = document.querySelector('.lpv2-sticky-add-to-cart .lpv2-add-to-cart-btn');
        if (stickyButton) stickyButton.disabled = false;

        // Step 3: Render cart drawer with full cart state + section HTML
        if (cartDrawerEl && typeof cartDrawerEl.renderContents === 'function') {
          cartDrawerEl.renderContents(cartDataWithSections);
        }

        // Dispatch events AFTER render so other components update against correct DOM state
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        document.dispatchEvent(new CustomEvent('cart:item-added'));
      })
      .catch((error) => {
        // Handle network or other errors
        console.error('[LPv2] Cart Add Error:', error);
        setAddToCartSkeleton(false);
        if (addToCartBtn) addToCartBtn.disabled = false;
        const stickyButton = document.querySelector('.lpv2-sticky-add-to-cart .lpv2-add-to-cart-btn');
        if (stickyButton) stickyButton.disabled = false;
      });
    }

    // Start the add to cart process
    setAddToCartSkeleton(true);
    doAddToCart();
  }

  /**
   * Handle notify me button click
   * @param {Event} e - Click event
   */
  function handleNotifyMe(e) {
    e.preventDefault();
    
    const state = stateManager.getState();
    if (!state.selectedVariant) return;

    // Update variant ID in notify me modal if it exists
    const notifyModal = document.querySelector('[data-modal="product-notify"]');
    if (notifyModal) {
      const variantInput = notifyModal.querySelector('input[name="variant_id"]');
      if (variantInput) {
        variantInput.value = state.selectedVariantId;
      }
    }

    // Trigger modal (assuming you have a modal system)
    const modalTrigger = document.querySelector('[data-modal-trigger="product-notify"]');
    if (modalTrigger) {
      modalTrigger.click();
    }
  }

  // Set up event listeners for all buttons
  if (addToCartBtn) {
    addToCartBtn.addEventListener('click', handleAddToCart);
  }
  
  const stickyButton = document.querySelector('.lpv2-sticky-add-to-cart .lpv2-add-to-cart-btn');
  if (stickyButton) {
    stickyButton.addEventListener('click', handleAddToCart);
  }

  if (notifyMeBtn) {
    notifyMeBtn.addEventListener('click', handleNotifyMe);
  }

  // Listen for state changes from other components
  document.addEventListener('lpv2-state-changed', function(e) {
    updateButtons(e.detail);
  });

  // Listen for freebies updates from the freebies slider
  document.addEventListener('freebies-updated', function(e) {
    // Check if freebies are enabled in theme customizer
    const enableFreebiesSwiper = window.LPV2MainProductInitialState?.enableFreebiesSwiper;
    const freebiesEnabled = enableFreebiesSwiper !== false && enableFreebiesSwiper !== "false";

    if (e.detail && e.detail.freebies) {
      // Update the state manager with the new freebies selection
      const selectedFreebieIds = freebiesEnabled ? e.detail.freebies
        .filter(f => f.selected)
        .map(f => f.id || f.variantId) : [];

      stateManager.setState({
        selectedFreebies: selectedFreebieIds
      });
    }
  });

  // Perform initial state update
  const initialState = stateManager.getState();
  if (quantityInput) {
    const syncQtyFromInput = () => {
      const q = parseInt(quantityInput.value, 10);
      if (!isNaN(q) && q > 0 && stateManager.getState().quantity !== q) {
        stateManager.setState({ quantity: q });
      }
    };
    quantityInput.addEventListener('change', syncQtyFromInput);
    quantityInput.addEventListener('input', syncQtyFromInput);

    const initialInputQty = parseInt(quantityInput.value, 10);
    if (!isNaN(initialInputQty) && initialInputQty > 0 && initialState.quantity !== initialInputQty) {
      stateManager.setState({ quantity: initialInputQty });
    }
  }
  updateButtons(initialState);

  /**
   * Update sticky add to cart button with current state
   * @param {Object} state - Current product state
   */
  function updateStickyAddToCart(state) {
    const stickyBtn = document.querySelector('.lpv2-sticky-add-to-cart .lpv2-add-to-cart-btn');
    if (!stickyBtn) return;
    
    // Update price display
    stickyBtn.querySelector('.lpv2-btn__price').textContent = formatMoney(state.discountedPrice, state.currency, state.locale);
    
    // Update compare at price display
    const compareAt = stickyBtn.querySelector('.lpv2-btn__compare_at_price');
    if (state.compareAtPrice > state.discountedPrice) {
      stickyBtn.classList.add('has-compare-price');
      if (compareAt) {
        compareAt.textContent = formatMoney(state.compareAtPrice, state.currency, state.locale);
        compareAt.style.display = '';
      }
    } else {
      stickyBtn.classList.remove('has-compare-price');
      if (compareAt) compareAt.style.display = 'none';
    }
    
    // Enable/disable based on availability
    stickyBtn.disabled = !state.available;
  }

  /**
   * Handle sticky button visibility based on main button position
   */
  function handleStickyVisibility() {
    const sticky = document.querySelector('.lpv2-sticky-add-to-cart');
    const mainBtn = document.querySelector('.lpv2-btn--add-to-cart');
    
    if (!sticky || !mainBtn) return;
    
    const rect = mainBtn.getBoundingClientRect();
    
    // Show sticky button when main button is out of view
    if (addToCartButtonWasSeen && (rect.bottom < 0 || rect.top > window.innerHeight)) {
      sticky.classList.remove('hidden');
    } else {
      sticky.classList.add('hidden');
    }
  }

  // Set up scroll and resize listeners for sticky button
  window.addEventListener('scroll', handleStickyVisibility);
  window.addEventListener('resize', handleStickyVisibility);

  // Update sticky button when state changes
  document.addEventListener('lpv2-state-changed', function(e) {
    updateStickyAddToCart(e.detail);
    handleStickyVisibility();
  });

  // Perform initial sticky button update
  updateStickyAddToCart(stateManager.getState());
} 